import React, { useEffect, useState, useMemo, useCallback } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";

const BASE_URL = "https://api-project360-insights.haqdarshak.com";

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 hover:text-indigo-600 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatGender = (gender) => {
  if (!gender) return "Other";
  const g = String(gender).toUpperCase();
  if (g === "M" || g === "MALE") return "Male";
  if (g === "F" || g === "FEMALE") return "Female";
  if (g === "O" || g === "OTHER") return "Other";
  return "Other";
};

const isActiveTrue = (val) => val === "Active" || val === true || val === 1 || val === "1";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch (e) {
    return "-";
  }
};

// Column configuration drives both the header (with grouping) and body rendering.
// width is a tailwind-style fixed px width so the table never needs horizontal scroll.
const COLUMNS = [
  { key: "HDID", group: null, label: "HD ID", width: 85, align: "left" },
  { key: "AMS_ID", group: null, label: "AMS ID", width: 85, align: "left" },
  { key: "HDName", group: null, label: "HD Name", width: 120, align: "left" },
  { key: "HDGender", group: null, label: "Gender", width: 90, align: "center" },
  { key: "HDCreatedDate", group: null, label: "HD Onboarded Date", width: 110, align: "center" },
  { key: "HD_Operating_State", group: "Location", label: "Operating State", width: 130, align: "left" },
  { key: "HD_Operating_District", group: "Location", label: "Operating District", width: 135, align: "left" },
  { key: "Age", group: null, label: "Age", width: 55, align: "center" },
  { key: "Manager_Name", group: null, label: "Manager Name", width: 120, align: "left" },
  { key: "is_active", group: null, label: "Active Status", width: 95, align: "center" },
  { key: "open_count", group: "Applications", label: "Opened", width: 80, align: "center" },
  { key: "ds_count", group: "Applications", label: "Submitted", width: 85, align: "center" },
  { key: "br_count", group: "Applications", label: "Benefit Received", width: 100, align: "center" },
  { key: "aborted_count", group: "Applications", label: "Aborted", width: 80, align: "center" },
];

// Build grouped header row from COLUMNS (consecutive same-group columns get a colSpan)
const buildHeaderGroups = (columns) => {
  const groups = [];
  columns.forEach((col) => {
    const last = groups[groups.length - 1];
    if (last && last.group === col.group && col.group !== null) {
      last.span += 1;
      last.width += col.width;
    } else {
      groups.push({ group: col.group, span: 1, width: col.width });
    }
  });
  return groups;
};

const HDReport = () => {
  const reduxPid = useSelector((state) => state.user.projectId);
  const [pid] = useState(reduxPid);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [activeStatus, setActiveStatus] = useState([]);
  const [managers, setManagers] = useState([]);
  const [genders, setGenders] = useState([]);

  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedActive, setSelectedActive] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);

  const [showManagerInfo, setShowManagerInfo] = useState(false);

  const rowsPerPageOptions = [5, 10, 20, 50, 100, 500, 1000, 10000];
  const headerGroups = useMemo(() => buildHeaderGroups(COLUMNS), []);

  // ---------------------------------------------------------------------
  // Export CSV
  // ---------------------------------------------------------------------
  const exportToCSV = useCallback(() => {
    if (filteredData.length === 0) return;

    const headers = [
      "HD ID", "AMS ID", "HD Name", "Gender",
      "Operating State", "Operating District", "Age",
      "Manager Name", "Onboarded Date", "Active HD",
      "Applications Opened", "Applications Submitted",
      "Applications with Benefit Received", "Applications Aborted",
    ];

    const csvData = filteredData.map((row) => [
      row.HDID, row.AMS_ID, row.HDName, formatGender(row.HDGender),
      row.HD_Operating_State, row.HD_Operating_District, row.Age,
      row.Manager_Name, formatDate(row.HDCreatedDate), row.is_active,
      row.open_count || 0, row.ds_count || 0, row.br_count || 0, row.aborted_count || 0,
    ]);

    const csvContent = [headers.join(","), ...csvData.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `HD_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredData]);

  // ---------------------------------------------------------------------
  // Params builder
  // ---------------------------------------------------------------------
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append("pid", pid);
    selectedStates.forEach((i) => params.append("HD_Operating_State", i.value));
    selectedDistricts.forEach((i) => params.append("HD_Operating_District", i.value));
    selectedActive.forEach((i) => params.append("is_active", i.value));
    selectedManagers.forEach((i) => params.append("Manager_Name", i.value));
    selectedGenders.forEach((i) => params.append("HDGender", i.value));
    return params.toString();
  }, [pid, selectedStates, selectedDistricts, selectedActive, selectedManagers, selectedGenders]);

  // ---------------------------------------------------------------------
  // Build empty params (for clearing filters)
  // ---------------------------------------------------------------------
  const buildEmptyParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append("pid", pid);
    return params.toString();
  }, [pid]);

  // ---------------------------------------------------------------------
  // Fetch filter dropdown options
  // ---------------------------------------------------------------------
  const fetchFilterOptions = useCallback(async (paramsString = null) => {
    setFiltersLoading(true);
    try {
      const params = paramsString !== null ? paramsString : buildParams();
      const [statesRes, districtsRes, activeRes, managersRes, gendersRes] = await Promise.all([
        fetch(`${BASE_URL}/kpi/hd-report/states/?${params}`),
        fetch(`${BASE_URL}/kpi/hd-report/districts/?${params}`),
        fetch(`${BASE_URL}/kpi/hd-report/active-status/?${params}`),
        fetch(`${BASE_URL}/kpi/hd-report/managers/?${params}`),
        fetch(`${BASE_URL}/kpi/hd-report/genders/?${params}`),
      ]);

      const [statesJson, districtsJson, activeJson, managersJson, gendersJson] = await Promise.all([
        statesRes.json(), districtsRes.json(), activeRes.json(), managersRes.json(), gendersRes.json(),
      ]);

      setStates((statesJson.HD_Operating_State || []).map((v) => ({ label: v, value: v })));
      setDistricts((districtsJson.HD_Operating_District || []).map((v) => ({ label: v, value: v })));
      setActiveStatus((activeJson.is_active || []).map((v) => ({ label: v, value: v })));
      setManagers((managersJson.Manager_Name || []).map((v) => ({ label: v, value: v })));
      setGenders(gendersJson.HDGender || []);
    } catch (err) {
      console.error("Error fetching filter options:", err);
    } finally {
      setFiltersLoading(false);
    }
  }, [buildParams]);

  // ---------------------------------------------------------------------
  // Fetch table data
  // ---------------------------------------------------------------------
  const fetchData = useCallback(async (paramsString = null) => {
    if (!pid) return;
    setLoading(true);
    setError(null);
    try {
      const params = paramsString !== null ? paramsString : buildParams();
      const res = await fetch(`${BASE_URL}/kpi/hd-report/data/?${params}`);
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Could not load HD data. Please try again.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams, pid]);

  // ---------------------------------------------------------------------
  // Search (client side)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(data);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    setFilteredData(
      data.filter((row) => Object.values(row).some((v) => String(v).toLowerCase().includes(term)))
    );
  }, [searchTerm, data]);

  // Display slice
  useEffect(() => {
    setDisplayData(filteredData.slice(0, rowsPerPage));
  }, [filteredData, rowsPerPage]);

  // Initial load: fetch filter options AND data immediately on mount
  useEffect(() => {
    if (!pid) return;
    fetchFilterOptions();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Stats
  const stats = useMemo(() => {
    const total = filteredData.length;
    const active = filteredData.filter((r) => isActiveTrue(r.is_active)).length;
    return { total, active, inactive: total - active };
  }, [filteredData]);

  // Clear filters - resets all selections and fetches with empty params
  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedDistricts([]);
    setSelectedActive([]);
    setSelectedManagers([]);
    setSelectedGenders([]);
    setSearchTerm("");
    
    // Use setTimeout to ensure state updates are complete before fetching
    setTimeout(() => {
      const emptyParams = buildEmptyParams();
      fetchData(emptyParams);
      fetchFilterOptions(emptyParams);
    }, 0);
  };

  // Apply filters - fetches data and filter options with current selections
  const applyFilters = () => {
    fetchData();
    fetchFilterOptions();
  };

  const hasActiveFilters =
    selectedStates.length || selectedDistricts.length || selectedActive.length ||
    selectedManagers.length || selectedGenders.length;

  // react-select theming
  const customSelectStyles = {
    control: (p, state) => ({
      ...p,
      borderRadius: "8px",
      borderColor: state.isFocused ? "#6366f1" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(99,102,241,0.12)" : "none",
      minHeight: "38px",
      fontSize: "13px",
      "&:hover": { borderColor: "#9ca3af" },
    }),
    menu: (p) => ({ ...p, borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }),
    menuPortal: (p) => ({ ...p, zIndex: 9999 }),
    option: (p, state) => ({
      ...p,
      fontSize: "13px",
      backgroundColor: state.isSelected ? "#eef2ff" : state.isFocused ? "#f9fafb" : "white",
      color: "#1f2937",
    }),
    placeholder: (p) => ({ ...p, fontSize: "13px", color: "#9ca3af" }),
    multiValue: (p) => ({ ...p, backgroundColor: "#eef2ff", borderRadius: "5px" }),
    multiValueLabel: (p) => ({ ...p, color: "#4338ca", fontSize: "12px" }),
    valueContainer: (p) => ({ ...p, padding: "2px 8px" }),
  };

  // Shared props so every <Select> renders its menu in a portal attached to <body>.
  const selectPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    menuPlacement: "auto",
  };

  // -----------------------------------------------------------------------
  // Cell renderer
  // -----------------------------------------------------------------------
  const renderCell = (row, col) => {
    const alignClass = col.align === "center" ? "text-center" : "text-left";

    switch (col.key) {
      case "HDID":
        return <span className="font-semibold text-gray-900">{row.HDID}</span>;
      case "HDName":
        return <span className="font-medium text-gray-900 truncate block">{row.HDName}</span>;
      case "HDGender": {
        const g = formatGender(row.HDGender);
        const cls =
          g === "Male" ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
          : g === "Female" ? "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200"
          : "bg-gray-100 text-gray-600 ring-1 ring-gray-200";
        return (
          <span className={`inline-flex justify-center px-2.5 py-0.5 rounded-full text-sm font-medium ${cls}`}>
            {g}
          </span>
        );
      }
      case "HDCreatedDate": {
        const formattedDate = formatDate(row.HDCreatedDate);
        return (
          <span className="text-gray-700 text-sm whitespace-nowrap">
            {formattedDate}
          </span>
        );
      }
      case "is_active": {
        const active = isActiveTrue(row.is_active);
        const label = row.is_active === true || row.is_active === 1 ? "Active" : row.is_active === false || row.is_active === 0 ? "Inactive" : row.is_active;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ring-1 ${
              active ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-rose-50 text-rose-700 ring-rose-200"
            }`}
          >
            {label}
          </span>
        );
      }
      case "open_count":
      case "ds_count":
      case "br_count":
      case "aborted_count":
        return <span className="tabular-nums text-gray-700">{row[col.key] || 0}</span>;
      default:
        return <span className="text-gray-600 truncate block">{row[col.key]}</span>;
    }
  };

  return (
    <div className="w-[90vw] mt-[2vh] bg-white border border-gray-200 shadow-sm rounded-xl p-5">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-white via-white to-white px-5 py-4 border border-gray-200 rounded-xl mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-pink-800 flex items-center gap-2">
              <span className="p-2 bg-pink-100 text-pink-700 rounded-lg">
                <UserIcon />
              </span>
              HD Summary Report
            </h2>
            <p className="text-sm text-gray-500 mt-1 ml-11">
              Filter, search, and export HD Data
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
          >
            <DownloadIcon />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {/* STATS */}
        <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-3">
          <div className="flex-1 sm:flex-none sm:w-40 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Total HDs Onboarded</div>
            <div className="text-lg font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="flex-1 sm:flex-none sm:w-40 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
            <div className="text-[11px] uppercase tracking-wide text-emerald-600 font-medium">HDs Active</div>
            <div className="text-lg font-bold text-emerald-700">{stats.active}</div>
          </div>
          <div className="flex-1 sm:flex-none sm:w-40 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5">
            <div className="text-[11px] uppercase tracking-wide text-rose-600 font-medium">HDs Inactive</div>
            <div className="text-lg font-bold text-rose-700">{stats.inactive}</div>
          </div>
        </div>

        {/* FILTERS PANEL */}
        <div className="bg-white border border-gray-200 rounded-xl relative z-20">
          <button
            onClick={() => setFiltersOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-xl"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <FilterIcon />
              Filters
              {hasActiveFilters ? (
                <span className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] rounded-full font-semibold">
                  {selectedStates.length + selectedDistricts.length + selectedActive.length + selectedManagers.length + selectedGenders.length}
                </span>
              ) : null}
              {filtersLoading && <span className="text-[11px] text-gray-400 font-normal">refreshing options…</span>}
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
          </button>

          {filtersOpen && (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 border-t border-gray-100">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Operating State</label>
                <Select 
                  isMulti 
                  options={states} 
                  value={selectedStates} 
                  onChange={setSelectedStates} 
                  styles={customSelectStyles} 
                  placeholder="All states" 
                  {...selectPortalProps} 
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Operating District</label>
                <Select 
                  isMulti 
                  options={districts} 
                  value={selectedDistricts} 
                  onChange={setSelectedDistricts} 
                  styles={customSelectStyles} 
                  placeholder="All districts" 
                  {...selectPortalProps} 
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Active Status</label>
                <Select 
                  isMulti 
                  options={activeStatus} 
                  value={selectedActive} 
                  onChange={setSelectedActive} 
                  styles={customSelectStyles} 
                  placeholder="All statuses" 
                  {...selectPortalProps} 
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block  items-center gap-1">
                  Manager
                  <span 
                    className="relative inline-block"
                    onMouseEnter={() => setShowManagerInfo(true)}
                    onMouseLeave={() => setShowManagerInfo(false)}
                  >
                    <InfoIcon />
                    {showManagerInfo && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50">
                        Current Manager of the HD
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </span>
                </label>
                <Select 
                  isMulti 
                  options={managers} 
                  value={selectedManagers} 
                  onChange={setSelectedManagers} 
                  styles={customSelectStyles} 
                  placeholder="All managers" 
                  {...selectPortalProps} 
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Gender</label>
                <Select 
                  isMulti 
                  options={genders} 
                  value={selectedGenders} 
                  onChange={setSelectedGenders} 
                  styles={customSelectStyles} 
                  placeholder="All genders" 
                  {...selectPortalProps} 
                />
              </div>

              <div className="md:col-span-2 lg:col-span-3 xl:col-span-5 flex gap-3 pt-1">
                <button
                  onClick={applyFilters}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <RefreshIcon />
                  {loading ? "Applying…" : "Apply Filters"}
                </button>
                <button
                  onClick={clearFilters}
                  className="px-5 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SEARCH */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search HDs by name, ID, state, district…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 text-sm transition-colors"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon />
          </div>
        </div>

        {/* TABLE CARD */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table toolbar */}
          <div className="px-4 py-2.5 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50">
            <span className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{displayData.length}</span> of{" "}
              <span className="font-semibold text-gray-900">{filteredData.length}</span> records
              {loading && <span className="ml-2 text-indigo-500">• loading…</span>}
            </span>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-medium">Rows:</label>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="px-2.5 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-400 bg-white"
              >
                {rowsPerPageOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 bg-rose-50 text-rose-700 text-sm border-b border-rose-100">{error}</div>
          )}

          {/* Table — fixed layout, no horizontal scroll; grouped 2-row header */}
          <div className="w-full">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                {COLUMNS.map((col) => (
                  <col key={col.key} style={{ width: `${col.width}px` }} />
                ))}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200 select-none">
                {/* Group row */}
                <tr>
                  {headerGroups.map((g, idx) =>
                    g.group ? (
                      <th
                        key={`grp-${idx}`}
                        colSpan={g.span}
                        className="px-2 py-1.5 text-center text-[11px] font-bold text-indigo-700 uppercase tracking-wide border-b border-indigo-100 bg-indigo-50/60 whitespace-normal break-words"
                      >
                        {g.group}
                      </th>
                    ) : (
                      <th key={`grp-${idx}`} className="bg-gray-50" />
                    )
                  )}
                </tr>
                {/* Label row */}
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-2 py-2 text-[11px] font-semibold text-gray-600 uppercase tracking-wider border-r last:border-r-0 border-gray-200 leading-tight whitespace-normal break-words ${
                        col.align === "center" ? "text-center" : "text-left"
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                      {COLUMNS.map((col) => (
                        <td key={col.key} className="px-2 py-2.5">
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4 mx-auto" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : displayData.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-gray-500 text-sm">
                      No records found{hasActiveFilters || searchTerm ? " for the current filters" : ""}.
                    </td>
                  </tr>
                ) : (
                  displayData.map((row, index) => (
                    <tr
                      key={row.HDID ?? index}
                      className={`hover:bg-indigo-50/40 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}
                    >
                      {COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          className={`px-2 py-2 border-r last:border-r-0 border-gray-50 overflow-hidden text-ellipsis whitespace-nowrap ${
                            col.align === "center" ? "text-center" : "text-left"
                          }`}
                          title={typeof row[col.key] === "string" ? row[col.key] : undefined}
                        >
                          {renderCell(row, col)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-sm text-gray-600">
            <div>
              Showing <span className="font-medium">{displayData.length}</span> of{" "}
              <span className="font-medium">{filteredData.length}</span> entries
            </div>
            <span className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded-md text-xs font-medium">
              {filteredData.length} Total
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HDReport;